import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, map, of, switchMap, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  ModuleVisibilityContent,
  VisibilitySection,
} from 'app/core/models/module-visibility.model';

@Injectable({
  providedIn: 'root',
})
export class ModuleVisibilityService {
  private _authUrl = environment.authUrlPrefix;
  private _instancePreferencesUrl = 'InstancePreference';
  private _assetType = 'MODULE_VISIBILITY';

  private _content: WritableSignal<ModuleVisibilityContent> = signal({});

  constructor(private _httpClient: HttpClient) {}

  get content(): Signal<ModuleVisibilityContent> {
    return this._content.asReadonly();
  }

  /**
   * Loads the stored map for the app shell. Never fails: on any error every
   * module stays visible, so a flaky request cannot hide features.
   */
  load(): Observable<ModuleVisibilityContent> {
    return this.loadForEditing().pipe(
      catchError(() => {
        this._content.set({});
        return of({} as ModuleVisibilityContent);
      })
    );
  }

  /**
   * Loads the stored map for the settings editor. A 404 means the instance has
   * no MODULE_VISIBILITY preference yet (everything visible), but any other
   * error is propagated: an editor that showed "all visible" after a failed
   * read would let Save overwrite the real configuration.
   */
  loadForEditing(): Observable<ModuleVisibilityContent> {
    const instanceId = this._resolveInstanceId();
    if (!instanceId) {
      this._content.set({});
      return of({});
    }
    return this._httpClient
      .post<any>(`${this._authUrl}${this._instancePreferencesUrl}/GetByInstance`, {
        instanceId,
        app: '*',
        appType: 'WEB',
        assetType: this._assetType,
      })
      .pipe(
        catchError((error: HttpErrorResponse) =>
          error?.status === 404 ? of(null) : throwError(() => error)
        ),
        map((record) => this._parseContent(record)),
        map((parsed) => {
          this._content.set(parsed);
          return parsed;
        })
      );
  }

  /**
   * Persist a full module-visibility map for the current instance. Looks up
   * whether a MODULE_VISIBILITY preference already exists (PUT) or not (POST),
   * then refreshes the in-memory signal so open lists react immediately.
   *
   * A failed lookup is propagated, not read as "none exists": falling through
   * to the POST then created a second MODULE_VISIBILITY preference.
   */
  save(content: ModuleVisibilityContent): Observable<ModuleVisibilityContent> {
    const instanceId = this._resolveInstanceId();
    if (!instanceId) {
      return of(content);
    }
    const serialized = JSON.stringify(content);

    return this._httpClient
      .get<any[]>(`${this._authUrl}${this._instancePreferencesUrl}`, { params: { instanceId } })
      .pipe(
        switchMap((preferences) => {
          const existing = (preferences ?? []).find((p) => p?.assetType === this._assetType);
          const body = {
            instanceId,
            app: '*',
            appType: 'WEB',
            assetType: this._assetType,
            content: serialized,
          };
          return existing?.id
            ? this._httpClient.put(
                `${this._authUrl}${this._instancePreferencesUrl}/${existing.id}`,
                body
              )
            : this._httpClient.post(`${this._authUrl}${this._instancePreferencesUrl}`, body);
        }),
        tap(() => this._content.set(content)),
        map(() => content)
      );
  }

  isVisible(module: string, section: VisibilitySection, key: string): boolean {
    const moduleCfg = this._content().modules?.[module];
    if (!moduleCfg) return true;
    const sectionCfg = moduleCfg[section];
    if (!sectionCfg) return true;
    return sectionCfg[key] !== false;
  }

  filterColumns<T extends { header?: string }>(module: string, columns: T[]): T[] {
    return columns.filter((c) => !c.header || this.isVisible(module, 'table', c.header));
  }

  filterOptions<T>(
    module: string,
    section: VisibilitySection,
    keyFn: (item: T) => string,
    items: T[]
  ): T[] {
    return items.filter((item) => this.isVisible(module, section, keyFn(item)));
  }

  private _resolveInstanceId(): string | null {
    return (
      sessionStorage.getItem('primecontrol_idInstance') ||
      localStorage.getItem('primecontrol_idInstance') ||
      null
    );
  }

  private _parseContent(record: any): ModuleVisibilityContent {
    if (!record?.content) return {};
    try {
      const parsed =
        typeof record.content === 'string' ? JSON.parse(record.content) : record.content;
      return (parsed as ModuleVisibilityContent) || {};
    } catch {
      return {};
    }
  }
}
